/**
 * Manage, Groups: one group (plan docs/plans/lms-plan.md 8.2, 5.3, 18.20,
 * 18.21; WP-43; R6, R19, G9, G14). Route /lms/manage/groups/:groupId.
 *
 * - The group: name, kind, active state; Edit (name, description),
 *   Deactivate (DELETE: is_active false, members and requirements kept as
 *   history; a profile's open assignments are cancelled) and Reactivate.
 * - Members: the active members the admin reaches (G14), with the count of
 *   the others at sites the admin does not reach (hidden_member_count, never
 *   named); Add people (UserPicker) and Remove (the row stays as history; the
 *   person's open profile assignments from this group are cancelled unless
 *   another profile requires the course, 8.2). "Show history" reads
 *   ?history=1.
 * - Required courses (profiles only): each course with its due window and
 *   whether it is published; Add a course (course plus due_days, 0 to 365,
 *   default 30) and Remove. The due window is fixed: remove and add again to
 *   change it (18.20).
 * - After every change the reconcile's summary is told as a toast; a
 *   reconcile that could not run ({ failed, code }) is an amber WARNING on
 *   the page, never an error: the change is saved and the nightly pass
 *   catches up (18.20, 18.21).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, History, Loader2, Pencil, Plus, Power, RotateCcw, Trash2, UsersRound } from 'lucide-react';
import ConfirmModal from '../../../../ui/ConfirmModal';
import EmptyState from '../../../../ui/EmptyState';
import { useToast } from '../../../../ui/Toast';
import { iconBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { apiErrorCode, apiErrorMessage } from '../../../api/client';
import {
  deactivateGroup,
  getGroup,
  removeGroupMember,
  removeGroupRequirement,
  updateGroup,
} from '../../../api/groups';
import type { GroupDetail, GroupMember, GroupRequirement, ReconcileResult } from '../../../api/groups';
import {
  cardCls,
  ErrorNote,
  formatDay,
  linkBtnCls,
  metaCls,
  Pill,
  peopleCount,
  personName,
  rowCls,
  sectionTitleCls,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  thCls,
  theadCls,
  WarningNote,
} from '../../../components/pickers/shared';
import AddMembersModal from './AddMembersModal';
import AddRequirementModal from './AddRequirementModal';
import EditGroupModal from './EditGroupModal';
import { KindPill } from './GroupList';
import { describeReconcile, GROUPS_BASE, reconcileWarning } from './reconcile';

type Pending =
  | { kind: 'member'; member: GroupMember }
  | { kind: 'requirement'; requirement: GroupRequirement }
  | { kind: 'deactivate' }
  | null;

export function GroupDetailPage() {
  const { groupId = '' } = useParams();
  const { toast } = useToast();

  const [detail, setDetail] = useState<GroupDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<{ message: string; notFound: boolean } | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [warning, setWarning] = useState<string | null>(null);
  const [adding, setAdding] = useState<'members' | 'requirement' | null>(null);
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const request = useRef(0);

  useEffect(() => {
    const id = ++request.current;
    setLoading(true);
    getGroup(groupId, { history: showHistory })
      .then((d) => {
        if (id !== request.current) return;
        setDetail(d);
        setLoadError(null);
      })
      .catch((err) => {
        if (id !== request.current) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        setLoadError({ message: apiErrorMessage(err, 'Could not load the group.'), notFound: status === 404 });
      })
      .finally(() => {
        if (id === request.current) setLoading(false);
      });
  }, [groupId, showHistory, reloadKey]);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  /** Tell what the change did, then read the group again. */
  const settled = useCallback(
    (done: string, reconcile: ReconcileResult | undefined) => {
      const warn = reconcileWarning(reconcile);
      setWarning(warn);
      const said = describeReconcile(reconcile);
      toast(warn ? `${done} The assignments will catch up tonight.` : said ? `${done} ${said}` : done, {
        type: warn ? 'info' : 'success',
      });
      reload();
    },
    [toast, reload]
  );

  if (loading && !detail) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
        Loading the group
      </div>
    );
  }

  if (loadError && !detail) {
    return (
      <div className="space-y-3">
        <Link to={GROUPS_BASE} className={linkBtnCls}>
          <ArrowLeft className="h-4 w-4" />
          All groups
        </Link>
        {loadError.notFound ? (
          <EmptyState icon={UsersRound} variant="card" title="Group not found" hint="It may belong to another network, or the link is wrong." />
        ) : (
          <ErrorNote>
            {loadError.message}{' '}
            <button type="button" className="font-medium underline" onClick={reload}>
              Retry
            </button>
          </ErrorNote>
        )}
      </div>
    );
  }

  if (!detail) return null;
  const { group, members, requirements } = detail;
  const isProfile = group.kind === 'profile';
  const active = group.is_active;

  const confirmPending = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.kind === 'member') {
        const r = await removeGroupMember(group.id, pending.member.user_id);
        settled(`Removed ${personName(pending.member)}.`, r.reconcile);
      } else if (pending.kind === 'requirement') {
        const r = await removeGroupRequirement(group.id, pending.requirement.course_id);
        settled(`Removed ${pending.requirement.title ?? 'the course'} from the requirements.`, r.reconcile);
      } else {
        const r = await deactivateGroup(group.id);
        settled(`Deactivated ${group.name}.`, r.reconcile);
      }
      setPending(null);
    } catch (err) {
      toast(apiErrorMessage(err, 'Could not make the change.'), { type: 'error' });
      if (apiErrorCode(err) === 'LMS_NOT_FOUND') reload();
    } finally {
      setBusy(false);
    }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      const r = await updateGroup(group.id, { is_active: true });
      settled(`Reactivated ${group.name}.`, r.reconcile);
    } catch (err) {
      toast(apiErrorMessage(err, 'Could not reactivate the group.'), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const pendingCopy = (() => {
    if (!pending) return { title: '', message: '', confirm: '' };
    if (pending.kind === 'member') {
      return {
        title: `Remove ${personName(pending.member)}?`,
        message: isProfile
          ? "Their open assignments from this profile are cancelled, unless another profile still requires the course. Completions are kept, and the membership stays in the history."
          : "The membership stays in the history. Nothing already sent to them changes.",
        confirm: 'Remove',
      };
    }
    if (pending.kind === 'requirement') {
      return {
        title: `Stop requiring ${pending.requirement.title ?? 'this course'}?`,
        message:
          "Open assignments this requirement created are cancelled, unless another profile still requires the course. Completions are kept.",
        confirm: 'Remove requirement',
      };
    }
    return {
      title: `Deactivate ${group.name}?`,
      message: isProfile
        ? "The profile takes no new members or courses, and the open assignments it created are cancelled unless another profile still requires the course. Members and courses stay as history; you can reactivate it."
        : "The group takes no new members and is no longer offered as an audience. Members stay as history; you can reactivate it.",
      confirm: 'Deactivate',
    };
  })();

  const memberIds = members.map((m) => m.user_id);

  return (
    <div className="space-y-4" data-testid="group-detail">
      <Link to={GROUPS_BASE} className={linkBtnCls}>
        <ArrowLeft className="h-4 w-4" />
        All groups
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
            <span className="min-w-0 truncate" data-testid="group-name">
              {group.name}
            </span>
            <KindPill kind={group.kind} />
            {active ? <Pill tone="green">Active</Pill> : <Pill tone="gray">Deactivated</Pill>}
          </h2>
          {group.description && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{group.description}</p>}
          <p className={`${metaCls} mt-1`}>
            {isProfile
              ? "Members are assigned every required course on their own, due the given number of days after they start."
              : "A saved audience: pick it in Assign to send a course to its members."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={secondaryBtnCls} onClick={() => setEditing(true)} data-testid="edit-group">
            <Pencil className="h-4 w-4" />
            Edit
          </button>
          {active ? (
            <button
              type="button"
              className={secondaryBtnCls}
              onClick={() => setPending({ kind: 'deactivate' })}
              data-testid="deactivate-group"
            >
              <Power className="h-4 w-4" />
              Deactivate
            </button>
          ) : (
            <button type="button" className={primaryBtnCls} onClick={reactivate} disabled={busy} data-testid="reactivate-group">
              <RotateCcw className="h-4 w-4" />
              Reactivate
            </button>
          )}
        </div>
      </div>

      {warning && <WarningNote testId="reconcile-warning">{warning}</WarningNote>}
      {!active && (
        <WarningNote>This group is deactivated: reactivate it before adding people or courses.</WarningNote>
      )}
      {loadError && <ErrorNote>{loadError.message}</ErrorNote>}

      {/* Members */}
      <section className="space-y-2" aria-labelledby="lms-group-members">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id="lms-group-members" className={sectionTitleCls}>
            Members <span className="font-normal text-gray-500 dark:text-gray-400">({members.length.toLocaleString()})</span>
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={secondaryBtnCls}
              onClick={() => setShowHistory((v) => !v)}
              aria-pressed={showHistory}
              data-testid="toggle-history"
            >
              <History className="h-4 w-4" />
              {showHistory ? 'Hide history' : 'Show history'}
            </button>
            <button
              type="button"
              className={primaryBtnCls}
              onClick={() => setAdding('members')}
              disabled={!active}
              data-testid="add-members-open"
            >
              <Plus className="h-4 w-4" />
              Add people
            </button>
          </div>
        </div>
        {detail.hidden_member_count > 0 && (
          <p className={metaCls} data-testid="hidden-members">
            {`${detail.hidden_member_count.toLocaleString()} more ${detail.hidden_member_count === 1 ? 'member is' : 'members are'} at sites you do not reach: counted, not shown.`}
          </p>
        )}
        {members.length === 0 ? (
          <div className={`${cardCls} text-sm text-gray-500 dark:text-gray-400`}>No members you can see yet.</div>
        ) : (
          <div className={tableCardCls}>
            <table className={tableCls}>
              <thead className={theadCls}>
                <tr>
                  <th scope="col" className={thCls}>
                    Person
                  </th>
                  <th scope="col" className={thCls}>
                    Added
                  </th>
                  <th scope="col" className={`${thCls} text-right`}>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className={tbodyCls}>
                {members.map((m) => (
                  <tr key={m.user_id} className={rowCls} data-testid={`member-row-${m.user_id}`}>
                    <td className={tdCls}>
                      <span className="flex flex-wrap items-center gap-2 font-medium">
                        {personName(m)}
                        {!m.is_active && <Pill tone="gray">Deactivated</Pill>}
                      </span>
                      {m.email && <span className={metaCls}>{m.email}</span>}
                    </td>
                    <td className={`${tdCls} whitespace-nowrap`}>{formatDay(m.added_at)}</td>
                    <td className={`${tdCls} text-right`}>
                      <button
                        type="button"
                        className={iconBtnCls}
                        aria-label={`Remove ${personName(m)}`}
                        title="Remove from the group"
                        onClick={() => setPending({ kind: 'member', member: m })}
                        data-testid={`remove-member-${m.user_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {showHistory && detail.history && (
          <div className={tableCardCls} data-testid="member-history">
            <table className={tableCls}>
              <caption className="px-4 pt-3 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Membership history</caption>
              <thead className={theadCls}>
                <tr>
                  <th scope="col" className={thCls}>
                    Person
                  </th>
                  <th scope="col" className={thCls}>
                    Added
                  </th>
                  <th scope="col" className={thCls}>
                    Removed
                  </th>
                </tr>
              </thead>
              <tbody className={tbodyCls}>
                {detail.history.map((h) => (
                  <tr key={h.id}>
                    <td className={tdCls}>{personName(h)}</td>
                    <td className={`${tdCls} whitespace-nowrap`}>{formatDay(h.added_at)}</td>
                    <td className={`${tdCls} whitespace-nowrap`}>{h.removed_at ? formatDay(h.removed_at) : <span className={metaCls}>Still a member</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Required courses (profiles only, 5.3) */}
      {isProfile && (
        <section className="space-y-2" aria-labelledby="lms-group-requirements">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 id="lms-group-requirements" className={sectionTitleCls}>
              Required courses{' '}
              <span className="font-normal text-gray-500 dark:text-gray-400">({requirements.length.toLocaleString()})</span>
            </h3>
            <button
              type="button"
              className={primaryBtnCls}
              onClick={() => setAdding('requirement')}
              disabled={!active}
              data-testid="add-requirement-open"
            >
              <Plus className="h-4 w-4" />
              Add a course
            </button>
          </div>
          {requirements.length === 0 ? (
            <div className={`${cardCls} text-sm text-gray-500 dark:text-gray-400`}>
              This profile requires no course yet. Add one and every member is assigned it.
            </div>
          ) : (
            <div className={tableCardCls}>
              <table className={tableCls}>
                <thead className={theadCls}>
                  <tr>
                    <th scope="col" className={thCls}>
                      Course
                    </th>
                    <th scope="col" className={thCls}>
                      Due within
                    </th>
                    <th scope="col" className={thCls}>
                      Course state
                    </th>
                    <th scope="col" className={`${thCls} text-right`}>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className={tbodyCls}>
                  {requirements.map((r) => (
                    <tr key={r.id} className={rowCls} data-testid={`requirement-row-${r.course_id}`}>
                      <td className={`${tdCls} font-medium`}>{r.title ?? 'Unknown course'}</td>
                      <td className={`${tdCls} whitespace-nowrap tabular-nums`}>{`${r.due_days} ${r.due_days === 1 ? 'day' : 'days'}`}</td>
                      <td className={tdCls}>
                        {!r.course_is_active ? (
                          <Pill tone="red">Retired</Pill>
                        ) : r.course_published ? (
                          <Pill tone="green">Published</Pill>
                        ) : (
                          <Pill tone="amber">Not published yet</Pill>
                        )}
                      </td>
                      <td className={`${tdCls} text-right`}>
                        <button
                          type="button"
                          className={iconBtnCls}
                          aria-label={`Stop requiring ${r.title ?? 'this course'}`}
                          title="Remove the requirement"
                          onClick={() => setPending({ kind: 'requirement', requirement: r })}
                          data-testid={`remove-requirement-${r.course_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className={metaCls}>To change a due window, remove the course and add it again.</p>
        </section>
      )}

      <AddMembersModal
        isOpen={adding === 'members'}
        groupId={group.id}
        groupName={group.name}
        memberIds={memberIds}
        onClose={() => setAdding(null)}
        onAdded={(r) => {
          setAdding(null);
          const extra = r.already_member > 0 ? ` ${peopleCount(r.already_member)} already in it.` : '';
          settled(`Added ${peopleCount(r.added)}.${extra}`, r.reconcile);
        }}
      />
      {isProfile && (
        <AddRequirementModal
          isOpen={adding === 'requirement'}
          groupId={group.id}
          groupName={group.name}
          requiredCourseIds={requirements.map((r) => r.course_id)}
          onClose={() => setAdding(null)}
          onAdded={(r) => {
            setAdding(null);
            settled(`Now requiring ${r.title}, due within ${r.due_days} ${r.due_days === 1 ? 'day' : 'days'}.`, r.reconcile);
          }}
        />
      )}
      <EditGroupModal
        isOpen={editing}
        group={group}
        onClose={() => setEditing(false)}
        onSaved={(r) => {
          setEditing(false);
          settled('Saved.', r.reconcile);
        }}
      />
      <ConfirmModal
        isOpen={pending !== null}
        onClose={() => (busy ? undefined : setPending(null))}
        onConfirm={confirmPending}
        title={pendingCopy.title}
        message={pendingCopy.message}
        confirmLabel={pendingCopy.confirm}
        danger
        loading={busy}
      />
    </div>
  );
}

export default GroupDetailPage;
